import { useState, useCallback } from 'react';

export interface FieldEditorState {
  editingField: { id: string; field: string; value: any; label: string } | null;
  editValue: string;
  isSaving: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export interface UseFieldEditorOptions<TItem extends { _id: string }> {
  onUpdate: (id: string, field: string, value: string) => Promise<void>;
  onDelete?: (id: string, field: string) => Promise<void>;
  onRefetch?: () => void | Promise<void>;
}

export interface UseFieldEditorReturn<TItem extends { _id: string }> {
  editingField: { id: string; field: string; value: any; label: string } | null;
  editValue: string;
  setEditValue: (value: string) => void;
  isSaving: boolean;
  handleEditField: (item: TItem, field: string, label: string) => void;
  handleCloseEditModal: () => void;
  handleSaveField: () => Promise<void>;
  handleDeleteField: () => Promise<void>;
}

/**
 * Generic hook for editing fields in a table/modal
 * Used for inline field editing in admin tables (Users, Posts, etc.)
 * 
 * @param options - Configuration object with update/delete handlers
 * @returns Field editor state and handlers
 * 
 * @example
 * const fieldEditor = useFieldEditor({
 *   onUpdate: async (id, field, value) => {
 *     await updateUser({ id, updates: { [field]: value } }).unwrap();
 *   },
 *   onRefetch: refetch
 * });
 */
export const useFieldEditor = <TItem extends { _id: string }>({
  onUpdate,
  onDelete,
  onRefetch,
}: UseFieldEditorOptions<TItem>): UseFieldEditorReturn<TItem> => {
  const [editingField, setEditingField] = useState<{ id: string; field: string; value: any; label: string } | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);

  const handleEditField = useCallback((item: TItem, field: string, label: string) => {
    const currentValue = (item as any)[field];
    setEditingField({ id: item._id, field, value: currentValue, label });
    setEditValue(currentValue || '');
  }, []);

  const handleCloseEditModal = useCallback(() => {
    setEditingField(null);
    setEditValue('');
  }, []);

  const handleSaveField = useCallback(async () => {
    if (!editingField) return;
    
    setIsSaving(true);
    try {
      const trimmedValue = editValue.trim();
      await onUpdate(editingField.id, editingField.field, trimmedValue || '');
      handleCloseEditModal();
      if (onRefetch) {
        await onRefetch();
      }
    } catch (error) {
      console.error('Error updating field:', error);
      alert('Failed to update field');
    } finally {
      setIsSaving(false);
    }
  }, [editingField, editValue, onUpdate, handleCloseEditModal, onRefetch]);

  const handleDeleteField = useCallback(async () => {
    if (!editingField) return;
    
    setIsSaving(true);
    try {
      if (onDelete) {
        await onDelete(editingField.id, editingField.field);
      } else {
        // Default behavior: set field to empty string
        await onUpdate(editingField.id, editingField.field, '');
      }
      handleCloseEditModal();
      if (onRefetch) {
        await onRefetch();
      }
    } catch (error) {
      console.error('Error deleting field:', error);
      alert('Failed to delete field');
    } finally {
      setIsSaving(false);
    }
  }, [editingField, onUpdate, onDelete, handleCloseEditModal, onRefetch]);

  return {
    editingField,
    editValue,
    setEditValue,
    isSaving,
    handleEditField,
    handleCloseEditModal,
    handleSaveField,
    handleDeleteField,
  };
};
